import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSearchTracker } from '../contexts/SearchContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Search, 
  Loader2, 
  X, 
  Upload, 
  Globe, 
  Star,
  ExternalLink,
  Check,
  XCircle,
  Undo2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Building2,
  Store,
  Save,
  Download,
  CheckSquare,
  Square,
  RotateCcw,
  SlidersHorizontal,
  Cloud,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';
import { Checkbox } from '../components/ui/checkbox';
import { EnrichmentPanel } from '../components/dashboard/EnrichmentPanel';
import { useEnrichmentTracker } from '../contexts/EnrichmentContext';
import { SubnicheEditorDialog } from '../components/dashboard/SubnicheEditorDialog';
import { MarketPicker } from '../components/dashboard/MarketPicker';
import {
  runSalesforceBulk, planSummary, mergeFinalResults, summaryLines, runTally, needsAttention,
} from '../components/dashboard/salesforceBulk';
import {
  MARKET_COUNTRIES, MACRO_CATEGORIES, MAX_CATEGORIES_PER_SEARCH, LEADS_PER_CATEGORY,
  REJECTION_REASONS, REJECTION_REASON_NEEDING_NOTE,
} from '../components/dashboard/constants';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

// Agent Check v2: a rejection needs a structured reason; "other" needs a note too. The
// backend validates the same rule and is the authority.
const rejectionReady = (code, note) => Boolean(code)
  && (code !== REJECTION_REASON_NEEDING_NOTE || Boolean((note || '').trim()));

// The reason picker shared by the single and the bulk reject dialogs: one required reason,
// then an optional note (required for "other").
function RejectionReasonFields({ idPrefix, code, onCodeChange, note, onNoteChange, notePlaceholder }) {
  const noteRequired = code === REJECTION_REASON_NEEDING_NOTE;
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-slate-200">Reason</Label>
        <RadioGroup
          value={code}
          onValueChange={onCodeChange}
          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          data-testid={`${idPrefix}-reason-group`}
        >
          {REJECTION_REASONS.map(({ code: value, label }) => (
            <div key={value} className="flex items-center gap-2">
              <RadioGroupItem
                value={value}
                id={`${idPrefix}-reason-${value}`}
                data-testid={`${idPrefix}-reason-${value}`}
              />
              <Label htmlFor={`${idPrefix}-reason-${value}`} className="text-sm font-normal text-slate-300">
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-reason`}>
          {noteRequired ? 'Note (required for Other)' : 'Note (optional)'}
        </Label>
        <Input
          id={`${idPrefix}-reason`}
          placeholder={notePlaceholder}
          value={note}
          onChange={(e) => onNoteChange(e.target.value.slice(0, 120))}
          data-testid={`${idPrefix}-reason-input`}
        />
        <p className="text-xs text-slate-400">{note.length}/120 characters</p>
      </div>
    </div>
  );
}

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

const Dashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // P9: the in-progress search now lives in SearchProvider so it survives navigation.
  // `searchProgress` keeps its old name locally so the rest of this component is untouched.
  const {
    isSearching,
    progress: searchProgress,
    finishedSearch,
    startSearch,
    clearFinishedSearch,
  } = useSearchTracker();
  const [searchResults, setSearchResults] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Form state
  const [categories, setCategories] = useState([]);
  // P8: neutral macro key per chip, index-aligned with `categories`. null for free-typed
  // chips. Always mutated together with `categories` so the two never drift apart.
  const [categoryKeys, setCategoryKeys] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [macroOpen, setMacroOpen] = useState(false);
  // P12b: macro whose sub-niche editor is open (null = closed), and the macro_keys the
  // server reports as customised for the current market. `customised` is ALWAYS read from
  // the API — never inferred by diffing arrays locally.
  const [editorMacro, setEditorMacro] = useState(null);
  const [customisedMacroKeys, setCustomisedMacroKeys] = useState([]);
  // M4-A: ONE market per search. The array shape is kept on purpose — `[]` or `[market]` —
  // so the request body (`countries`) and the sub-niche editor (`countries[0]`) are unchanged.
  const [countries, setCountries] = useState([]);
  // S12 slice 1 (product decision, 10 Oct 2026): both switches are opt-in — off for every new
  // search; the user turns them on for the search at hand.
  const [includeMarketplaces, setIncludeMarketplaces] = useState(false);
  // "Include brand shops": brands' and manufacturers' own online stores are excluded unless the
  // user switches them on for this search.
  const [includeBrandShops, setIncludeBrandShops] = useState(false);

  // P6.8: persistent, company-scoped blacklist. blacklistInfo mirrors the server state
  // ({ source_filename, domains_count }); it survives across searches and sessions.
  const [blacklistInfo, setBlacklistInfo] = useState(null);
  const [blacklistUploading, setBlacklistUploading] = useState(false);
  const [agentCheck, setAgentCheck] = useState('');

  // Decision state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedLeadForReject, setSelectedLeadForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');          // the optional note
  const [rejectReasonCode, setRejectReasonCode] = useState('');  // Agent Check v2: required code
  const [processingDecision, setProcessingDecision] = useState(null);

  // Bulk selection state
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [bulkRejectReasonCode, setBulkRejectReasonCode] = useState('');
  const [processingBulk, setProcessingBulk] = useState(false);
  // P4.5: target lead IDs for the reject dialog (used by both selected-bulk and "Reject all pending")
  const [bulkRejectTargetIds, setBulkRejectTargetIds] = useState([]);

  // Reset review state
  const [resetReviewDialogOpen, setResetReviewDialogOpen] = useState(false);
  const [processingReset, setProcessingReset] = useState(false);

  // 2.0X — the list READS which leads are enriching. It owns nothing: the same map the
  // panel reads, so the two can never disagree about whether a run is in flight.
  // BG1 — bulk enrichment STARTS runs through the same owner as the panel.
  const {
    isEnriching: isLeadEnriching, jobs: enrichmentJobs, startEnrichment,
  } = useEnrichmentTracker();

  // Enrichment state
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  // P4.7: bulk enrichment state
  const [bulkEnrichEligible, setBulkEnrichEligible] = useState(null); // { lead_ids, total_approved, already_enriched }
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkEnrichProgress, setBulkEnrichProgress] = useState({ done: 0, total: 0, succeeded: 0, failed: 0 });
  const bulkEnrichCancelRef = useRef(false);

  // ============== P-SF5: bulk Salesforce ==============
  // Whether this company has the integration at all. One local read; anything other than a
  // clean answer hides the action entirely, exactly as the panel hides its own controls.
  const [salesforceOffered, setSalesforceOffered] = useState(false);
  const [sfDialogOpen, setSfDialogOpen] = useState(false);
  // The server's plan for the selection: the one authority on what each lead's state is.
  // 'error' when it could not be read.
  const [sfPlan, setSfPlan] = useState(null);
  const [sfPlanLoading, setSfPlanLoading] = useState(false);
  const [sfCampaigns, setSfCampaigns] = useState(null);      // array | 'unavailable'
  const [sfCampaignId, setSfCampaignId] = useState('');
  const [sfRunning, setSfRunning] = useState(false);
  const [sfProgress, setSfProgress] = useState(null);        // { started, total, results }
  const [sfFinal, setSfFinal] = useState(null);              // merged, after the run
  const [sfStopCode, setSfStopCode] = useState(null);
  // Which leads have unsaved enrichment edits, reported by their own open panel. The form
  // state itself is never copied here — only the fact that it is dirty.
  const [dirtyLeadIds, setDirtyLeadIds] = useState(new Set());
  const sfCancelRef = useRef(false);
  const sfMountedRef = useRef(true);
  // The ids the dialog is about, frozen when it opens: the run must be about what the user
  // saw, and the selection itself is never cleared by any of this.
  const sfIdsRef = useRef([]);

  useEffect(() => {
    sfMountedRef.current = true;
    return () => { sfMountedRef.current = false; };
  }, []);

  const handleLeadDirtyChange = useCallback((leadId, isDirty) => {
    setDirtyLeadIds((current) => {
      if (current.has(leadId) === isDirty) return current;
      const next = new Set(current);
      if (isDirty) next.add(leadId); else next.delete(leadId);
      return next;
    });
  }, []);

  // P9: polling and the resume-a-running-search logic moved to SearchProvider, so they keep
  // running while the user is on another page. This component only renders the results.

  // Load a specific search from the ?search=<id> parameter (Reports navigation, and the
  // "View results" action on the completion notification — one single mechanism).
  useEffect(() => {
    const searchId = searchParams.get('search');
    if (!searchId || !token) return;

    // P9: opening a specific search is an explicit choice — resolve the pending
    // finished-search marker so the effect below cannot override it with a different search.
    clearFinishedSearch();

    setLoadingSearch(true);
    axios.get(`${API_URL}/searches/${searchId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      setSearchResults(response.data);
      setSearchParams({});
    })
    .catch(error => {
      toast.error('Failed to load search');
      setSearchParams({});
    })
    .finally(() => {
      setLoadingSearch(false);
    });
  }, [searchParams, token, setSearchParams, clearFinishedSearch]);

  // P9: show the results of the search that just finished — whether it finished while the
  // user was watching this page, or while they were elsewhere and have now come back.
  // Fetches that ONE search by id; never the full list.
  useEffect(() => {
    if (!token || !finishedSearch || searchParams.get('search')) return;
    if (finishedSearch.status !== 'completed') return;
    if (searchResults?.id === finishedSearch.id) return;

    let cancelled = false;
    axios.get(`${API_URL}/searches/${finishedSearch.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      if (!cancelled) setSearchResults(response.data);
    })
    .catch(() => {});

    return () => { cancelled = true; };
  }, [finishedSearch, token, searchResults, searchParams]);

  // Validation
  const isFormValid = categories.length > 0 && countries.length > 0;

  // P10: each category runs its own full search, so the count is capped. The backend
  // rejects anything above the cap; this just stops the user reaching that error.
  const categoriesFull = categories.length >= MAX_CATEGORIES_PER_SEARCH;

  const warnCategoryLimit = () => {
    toast.error(`Up to ${MAX_CATEGORIES_PER_SEARCH} categories per search`);
  };

  // P8: add a macro — the chip shows the label and carries the neutral key. Shared by the
  // dropdown and by typing a macro label, so both produce an identical chip.
  const handleAddMacroCategory = (macro) => {
    if (!categoryKeys.includes(macro.key) && !categories.includes(macro.label)) {
      if (categoriesFull) {
        warnCategoryLimit();
        setMacroOpen(false);
        return;
      }
      setCategories([...categories, macro.label]);
      setCategoryKeys([...categoryKeys, macro.key]);
    }
    setMacroOpen(false);
  };

  // P12b: the editor works on the FIRST selected country — the one the engine actually
  // searches. No country selected means no market, so the editor stays closed.
  const editorCountry = countries[0] || null;

  // P12b: which macros this company has customised for the current market. Read from the
  // server, never derived locally, and refreshed after every save or reset.
  const loadCustomisedMacros = useCallback(async () => {
    if (!token || !editorCountry) {
      setCustomisedMacroKeys([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/subniche-overrides`, {
        params: { country: editorCountry },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomisedMacroKeys(
        (res.data.macros || []).filter(m => m.customised).map(m => m.macro_key)
      );
    } catch (error) {
      // Non-fatal: the indicator dot is cosmetic, the editor still opens and reports
      // its own errors.
      setCustomisedMacroKeys([]);
    }
  }, [token, editorCountry]);

  useEffect(() => {
    loadCustomisedMacros();
  }, [loadCustomisedMacros]);

  const handleAddCategory = (e) => {
    if (e.key === 'Enter' && categoryInput.trim()) {
      e.preventDefault();
      const typed = categoryInput.trim();

      // P8: a typed entry that exactly matches a macro label (trimmed, case-insensitive) is
      // treated as if it had been picked from the dropdown — same chip, same neutral key —
      // so the user gets the fan-out either way. Any OTHER typed text is unchanged below.
      const macro = MACRO_CATEGORIES.find(
        m => m.label.toLowerCase() === typed.toLowerCase()
      );
      if (macro) {
        handleAddMacroCategory(macro);
        setCategoryInput('');
        return;
      }

      if (!categories.includes(typed)) {
        if (categoriesFull) {
          warnCategoryLimit();
          return;
        }
        setCategories([...categories, typed]);
        // Free-typed chip carries no neutral key.
        setCategoryKeys([...categoryKeys, null]);
      }
      setCategoryInput('');
    }
  };

  // Removal is by INDEX so the label and its key are always dropped together.
  const handleRemoveCategory = (index) => {
    setCategories(categories.filter((_, i) => i !== index));
    setCategoryKeys(categoryKeys.filter((_, i) => i !== index));
  };

  // M4-A: the picker is single-select. Choosing a market REPLACES the previous one, and
  // clearing yields the empty array the validation already understands.
  const handleMarketChange = (market) => {
    setCountries(market ? [market] : []);
  };

  // P6.8: load the company's current blacklist on mount (persistence across sessions).
  useEffect(() => {
    if (!token) return;
    const loadBlacklist = async () => {
      try {
        const res = await axios.get(`${API_URL}/blacklist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data && res.data.domains_count > 0) {
          setBlacklistInfo(res.data);
        }
      } catch (error) {
        // Non-fatal: the form still works without a blacklist.
      }
    };
    loadBlacklist();
  }, [token]);

  // P6.8: upload .xlsx/.csv → parsed + stored server-side; REPLACES any existing blacklist.
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // reset so re-selecting the same file re-triggers change
    if (!file) return;

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.csv')) {
      toast.error('Please upload an .xlsx or .csv file');
      return;
    }

    setBlacklistUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/blacklist`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const { domains_count, skipped_count, source_filename } = res.data;
      setBlacklistInfo({ source_filename, domains_count });
      let msg = `${domains_count} domain${domains_count === 1 ? '' : 's'} loaded`;
      if (skipped_count > 0) {
        msg += ` · ${skipped_count} row${skipped_count === 1 ? '' : 's'} skipped`;
      }
      toast.success(msg);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload blacklist';
      toast.error(message);
    } finally {
      setBlacklistUploading(false);
    }
  };

  // P6.8: clear the company's blacklist (the X).
  const handleRemoveBlacklist = async () => {
    try {
      await axios.delete(`${API_URL}/blacklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlacklistInfo(null);
      toast.success('Blacklist cleared');
    } catch (error) {
      toast.error('Failed to clear blacklist');
    }
  };

  const handleSearch = async () => {
    if (!isFormValid) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Start the search (returns immediately with search_id)
      const response = await axios.post(
        `${API_URL}/searches`,
        {
          categories,
          // P8: index-aligned neutral macro keys; null for free-typed entries.
          category_keys: categoryKeys,
          countries,
          include_marketplaces: includeMarketplaces,
          include_brand_shops: includeBrandShops,
          agent_check: agentCheck || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const searchId = response.data.id;
      // P9: hand the search to the provider — it owns polling from here and keeps tracking
      // it across navigation.
      startSearch(searchId);
      toast.info('Search started. This may take 1-3 minutes...');

    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start search';
      toast.error(message);
    }
  };

  const handleNewSearch = () => {
    // Reset all form state to defaults
    setCategories([]);
    setCategoryKeys([]);
    setCategoryInput('');
    setCountries([]);
    setIncludeMarketplaces(false);
    setIncludeBrandShops(false);
    // Blacklist is company-scoped and persists across searches — do NOT clear it here.
    setAgentCheck('');
    // Clear results to show fresh form
    setSearchResults(null);
    setExpandedLeadId(null);
    // P9: also drop the finished-search marker, otherwise the effect above would immediately
    // re-load the results the user just dismissed.
    clearFinishedSearch();
  };

  // Decision handlers
  const handleApprove = async (leadId) => {
    setProcessingDecision(leadId);
    try {
      await axios.post(
        `${API_URL}/searches/${searchResults.id}/decisions`,
        { lead_id: leadId, decision: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setSearchResults(prev => ({
        ...prev,
        leads: prev.leads.map(lead => 
          lead.id === leadId ? { ...lead, decision: 'approved' } : lead
        )
      }));
      toast.success('Lead approved');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to approve lead';
      toast.error(message);
    } finally {
      setProcessingDecision(null);
    }
  };

  const handleRejectClick = (lead) => {
    setSelectedLeadForReject(lead);
    setRejectReason('');
    setRejectReasonCode('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedLeadForReject || !rejectionReady(rejectReasonCode, rejectReason)) return;
    const reasonCode = rejectReasonCode;
    const note = rejectReason.trim() || null;
    
    setProcessingDecision(selectedLeadForReject.id);
    setRejectDialogOpen(false);
    
    try {
      await axios.post(
        `${API_URL}/searches/${searchResults.id}/decisions`,
        {
          lead_id: selectedLeadForReject.id,
          decision: 'rejected',
          rejection_reason_code: reasonCode,
          rejection_reason: note
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setSearchResults(prev => ({
        ...prev,
        leads: prev.leads.map(lead =>
          lead.id === selectedLeadForReject.id
            ? { ...lead, decision: 'rejected', rejection_reason: note, rejection_reason_code: reasonCode }
            : lead
        )
      }));
      toast.success('Lead rejected');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reject lead';
      toast.error(message);
    } finally {
      setProcessingDecision(null);
      setSelectedLeadForReject(null);
      setRejectReason('');
      setRejectReasonCode('');
    }
  };

  const handleUndo = async (leadId) => {
    setProcessingDecision(leadId);
    // Close enrichment panel if open for this lead
    if (expandedLeadId === leadId) {
      setExpandedLeadId(null);
    }
    try {
      await axios.delete(
        `${API_URL}/searches/${searchResults.id}/decisions/${leadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setSearchResults(prev => ({
        ...prev,
        leads: prev.leads.map(lead =>
          lead.id === leadId
            ? { ...lead, decision: null, rejection_reason: null, rejection_reason_code: null }
            : lead
        )
      }));
      toast.success('Decision undone');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to undo decision';
      toast.error(message);
    } finally {
      setProcessingDecision(null);
    }
  };

  const toggleEnrichmentPanel = (leadId) => {
    setExpandedLeadId(expandedLeadId === leadId ? null : leadId);
  };

  // Bulk selection handlers
  const handleSelectLead = (leadId, checked) => {
    setSelectedLeadIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(leadId);
      } else {
        newSet.delete(leadId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked && searchResults) {
      // Select all visible leads
      const allIds = searchResults.leads.map(l => l.id);
      setSelectedLeadIds(new Set(allIds));
    } else {
      setSelectedLeadIds(new Set());
    }
  };

  const handleSelectAllPending = () => {
    if (searchResults) {
      const pendingIds = searchResults.leads
        .filter(l => !l.decision)
        .map(l => l.id);
      setSelectedLeadIds(new Set(pendingIds));
    }
  };

  // P4.5: shared approve loop that takes an explicit list of lead IDs
  const approveLeadsByIds = async (leadIds, source = 'bulk') => {
    if (!leadIds || leadIds.length === 0 || processingBulk) return;

    setProcessingBulk(true);
    console.log(`[BULK DEBUG] action=approve, source=${source}, count=${leadIds.length}`);

    let successCount = 0;
    let errorCount = 0;

    for (const leadId of leadIds) {
      // Skip already decided leads (defensive)
      const lead = searchResults.leads.find(l => l.id === leadId);
      if (lead?.decision) continue;

      try {
        await axios.post(
          `${API_URL}/searches/${searchResults.id}/decisions`,
          { lead_id: leadId, decision: 'approved' },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Update local state
        setSearchResults(prev => ({
          ...prev,
          leads: prev.leads.map(l =>
            l.id === leadId ? { ...l, decision: 'approved' } : l
          )
        }));
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to approve lead ${leadId}:`, error);
      }
    }

    setSelectedLeadIds(new Set());
    setProcessingBulk(false);

    if (successCount > 0) {
      toast.success(`Approved ${successCount} lead${successCount !== 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to approve ${errorCount} lead${errorCount !== 1 ? 's' : ''}`);
    }
  };

  // P4.5: shared reject loop that takes an explicit list of lead IDs and a reason.
  // Agent Check v2: one structured reason code + optional note, the same for every lead.
  const rejectLeadsByIds = async (leadIds, reasonCode, reason, source = 'bulk') => {
    if (!leadIds || leadIds.length === 0 || processingBulk) return;
    if (!rejectionReady(reasonCode, reason)) return;

    setProcessingBulk(true);
    setBulkRejectDialogOpen(false);
    const reasonToApply = (reason || '').trim() || null;
    console.log(`[BULK DEBUG] action=reject, source=${source}, count=${leadIds.length}, reason_code=${reasonCode}, reason_length=${reasonToApply?.length || 0}`);

    let successCount = 0;
    let errorCount = 0;

    for (const leadId of leadIds) {
      const lead = searchResults.leads.find(l => l.id === leadId);
      if (lead?.decision) continue;

      try {
        await axios.post(
          `${API_URL}/searches/${searchResults.id}/decisions`,
          {
            lead_id: leadId,
            decision: 'rejected',
            rejection_reason_code: reasonCode,
            rejection_reason: reasonToApply
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setSearchResults(prev => ({
          ...prev,
          leads: prev.leads.map(l =>
            l.id === leadId
              ? { ...l, decision: 'rejected', rejection_reason: reasonToApply, rejection_reason_code: reasonCode }
              : l
          )
        }));
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to reject lead ${leadId}:`, error);
      }
    }

    setSelectedLeadIds(new Set());
    setBulkRejectReason('');
    setBulkRejectReasonCode('');
    setBulkRejectTargetIds([]);
    setProcessingBulk(false);

    if (successCount > 0) {
      toast.success(`Rejected ${successCount} lead${successCount !== 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to reject ${errorCount} lead${errorCount !== 1 ? 's' : ''}`);
    }
  };

  const handleBulkApprove = async () => {
    await approveLeadsByIds(Array.from(selectedLeadIds), 'selected');
  };

  // P4.5: "Approve all pending" — operates on every lead with no decision yet
  const handleApproveAllPending = async () => {
    if (!searchResults) return;
    const pendingIds = searchResults.leads
      .filter(l => !l.decision)
      .map(l => l.id);
    await approveLeadsByIds(pendingIds, 'all-pending');
  };

  // P4.5: opens the existing reject reason dialog, scoped to all pending leads
  const handleOpenRejectAllPending = () => {
    if (!searchResults) return;
    const pendingIds = searchResults.leads
      .filter(l => !l.decision)
      .map(l => l.id);
    if (pendingIds.length === 0) return;
    setBulkRejectTargetIds(pendingIds);
    setBulkRejectDialogOpen(true);
  };

  // P4.5: opens the existing reject reason dialog, scoped to currently selected leads
  const handleOpenRejectSelected = () => {
    setBulkRejectTargetIds(Array.from(selectedLeadIds));
    setBulkRejectDialogOpen(true);
  };

  const handleBulkRejectConfirm = async () => {
    // P4.5: use bulkRejectTargetIds (set by either selected-bulk or all-pending entry point)
    const targets = bulkRejectTargetIds.length > 0
      ? bulkRejectTargetIds
      : Array.from(selectedLeadIds);
    await rejectLeadsByIds(targets, bulkRejectReasonCode, bulkRejectReason, targets === bulkRejectTargetIds ? 'all-pending-or-selected' : 'selected');
  };

  // Reset review handler
  const handleResetReview = async () => {
    if (!searchResults || processingReset) return;
    
    setProcessingReset(true);
    try {
      await axios.post(
        `${API_URL}/searches/${searchResults.id}/reset-review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state - reset all decisions to null
      setSearchResults(prev => ({
        ...prev,
        leads: prev.leads.map(l => ({ ...l, decision: null, rejection_reason: null, rejection_reason_code: null }))
      }));
      
      // Clear selection
      setSelectedLeadIds(new Set());
      
      toast.success('Review reset');
      setResetReviewDialogOpen(false);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Search not found');
      } else if (error.response?.status === 409) {
        toast.error('Cannot reset a running search');
      } else {
        toast.error('Failed to reset review');
      }
    } finally {
      setProcessingReset(false);
    }
  };

  // ============== P4.7: Bulk Enrichment ==============

  // Fetch which approved leads still need enrichment
  const fetchBulkEnrichEligibility = useCallback(async () => {
    if (!searchResults?.id || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/searches/${searchResults.id}/bulk-enrich-eligible`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBulkEnrichEligible(response.data);
    } catch (error) {
      // Silently swallow — button will simply not appear if we couldn't fetch
      console.error('Failed to fetch bulk-enrich eligibility:', error);
    }
  }, [searchResults?.id, token]);

  // Stable count of approved leads — used as a dependency to re-trigger eligibility checks.
  const approvedLeadCount = (searchResults?.leads ?? []).filter(l => l.decision === 'approved').length;

  // Re-fetch eligibility whenever the set of approved leads changes
  useEffect(() => {
    fetchBulkEnrichEligibility();
  }, [fetchBulkEnrichEligibility, approvedLeadCount]);

  const handleBulkEnrichCancel = () => {
    bulkEnrichCancelRef.current = true;
  };

  // P4.8c: UI-only mark this lead as not-enriched in local state, so the badge flips back to "⏳ Pending enrichment"
  const handleEnrichmentClearedLocal = useCallback((leadId) => {
    setBulkEnrichEligible(prev => {
      if (!prev) return prev;
      if (prev.lead_ids.includes(leadId)) return prev;
      return {
        ...prev,
        lead_ids: [...prev.lead_ids, leadId],
        already_enriched: Math.max(0, (prev.already_enriched || 0) - 1),
      };
    });
  }, []);

  // 2.0X — a run that finishes while its panel is CLOSED still updates the list: nothing
  // else would call this, because the panel that used to is not mounted. Keyed on the jobs
  // map, so it fires once per completion.
  useEffect(() => {
    const finished = Object.values(enrichmentJobs || {})
      .some((job) => job.status === 'done' || job.status === 'error');
    if (finished) {
      fetchBulkEnrichEligibility();
    }
  }, [enrichmentJobs, fetchBulkEnrichEligibility]);

  const handleBulkEnrich = async () => {
    if (!searchResults?.id || bulkEnriching) return;
    const ids = bulkEnrichEligible?.lead_ids || [];
    if (ids.length === 0) return;

    bulkEnrichCancelRef.current = false;
    setBulkEnriching(true);
    setBulkEnrichProgress({ done: 0, total: ids.length, succeeded: 0, failed: 0 });

    let succeeded = 0;
    let failed = 0;

    // Sequential — one request at a time, mandatory for rate-limit + legal-first posture
    // BG1 — one JOB at a time: each lead starts the server job the panel starts, and the loop
    // waits for that job to end before the next lead begins.
    for (let i = 0; i < ids.length; i++) {
      if (bulkEnrichCancelRef.current) break;
      const leadId = ids[i];
      // 2.0X — a lead already enriching from the panel is SKIPPED, not queued. Bulk and the
      // panel are the two entry points, and a second run on the same lead costs up to four
      // SerpApi requests for an answer we are already waiting for.
      if (isLeadEnriching(leadId)) {
        continue;
      }
      // BG1 — never the synchronous route. The server's active-job guard decides: a lead that
      // another tab or user is already enriching is followed to its end, not paid for twice.
      // `quiet` keeps per-lead toasts out of a bulk run; the summary below reports it once.
      const lead = searchResults.leads.find((item) => item.id === leadId) || { id: leadId };
      const outcome = await startEnrichment(lead, { quiet: true });
      if (outcome === 'done') {
        succeeded++;
      } else if (outcome === 'error') {
        // Skip silently on per-lead failure (robots.txt, timeout, etc.)
        failed++;
        console.warn(`Bulk enrich failed for lead ${leadId}`);
      }
      setBulkEnrichProgress({
        done: i + 1,
        total: ids.length,
        succeeded,
        failed,
      });
      // P4.8a-1: refresh badges live as the queue progresses (best-effort, non-blocking on UX)
      // Skip refresh on the last iteration since we'll re-fetch unconditionally below.
      if (i < ids.length - 1 && !bulkEnrichCancelRef.current) {
        await fetchBulkEnrichEligibility();
      }
    }

    const wasCancelled = bulkEnrichCancelRef.current;
    bulkEnrichCancelRef.current = false;
    setBulkEnriching(false);

    if (wasCancelled) {
      toast.info(`Cancelled — enriched ${succeeded} of ${ids.length} leads`);
    } else if (failed === 0) {
      toast.success(`Enriched ${succeeded} lead${succeeded !== 1 ? 's' : ''} successfully`);
    } else {
      toast.success(`Enriched ${succeeded} of ${ids.length} leads`);
    }

    // Refresh eligibility so the button updates / hides
    await fetchBulkEnrichEligibility();
  };

  // ============== P-SF5: Bulk Salesforce ==============

  // Is the integration there for this company at all? One LOCAL read — it calls no
  // Salesforce — and anything but a clean answer hides the action, exactly as the panel
  // hides its own Salesforce controls.
  useEffect(() => {
    if (!token || !searchResults?.id) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/salesforce/connection`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { if (!cancelled) setSalesforceOffered(true); })
      .catch(() => { if (!cancelled) setSalesforceOffered(false); });
    return () => { cancelled = true; };
  }, [token, searchResults?.id]);

  // What the SERVER knows about the selected leads. Read when the dialog opens, again when
  // the Campaign changes (each line then carries that pair's membership), and again when the
  // run ends — that last read is what the summary is built from.
  const fetchSalesforcePlan = useCallback(async (campaignId) => {
    setSfPlanLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/salesforce/bulk/lead-states`,
        {
          lead_ids: sfIdsRef.current,
          ...(campaignId ? { campaign_id: campaignId } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (sfMountedRef.current) setSfPlan(response.data);
      return response.data;
    } catch (error) {
      if (sfMountedRef.current) setSfPlan('error');
      return null;
    } finally {
      if (sfMountedRef.current) setSfPlanLoading(false);
    }
  }, [token]);

  // The org's active Campaigns, through the endpoint the panel already uses. A list that
  // cannot be read never blocks anything: the Campaign is optional.
  const fetchSalesforceCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/salesforce/campaigns`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (sfMountedRef.current) {
        setSfCampaigns(Array.isArray(response.data?.campaigns) ? response.data.campaigns : []);
      }
    } catch (error) {
      if (sfMountedRef.current) setSfCampaigns('unavailable');
    }
  }, [token]);

  const openSalesforceDialog = async () => {
    // The ids are frozen here: the dialog, the run and the summary are all about the
    // selection the user was looking at when they opened it.
    sfIdsRef.current = Array.from(selectedLeadIds);
    setSfPlan(null);
    setSfFinal(null);
    setSfProgress(null);
    setSfStopCode(null);
    setSfCampaignId('');
    setSfCampaigns(null);
    setSfDialogOpen(true);
    fetchSalesforceCampaigns();
    await fetchSalesforcePlan('');
  };

  const handleSalesforceCampaignChange = async (campaignId) => {
    setSfCampaignId(campaignId);
    await fetchSalesforcePlan(campaignId);
  };

  // Cancel stops the run from STARTING another lead. The lead in flight is always waited
  // for: a Salesforce write cannot be called back, and pretending otherwise would only lose
  // its answer.
  const handleSalesforceCancel = () => { sfCancelRef.current = true; };

  const handleSendToSalesforce = async () => {
    if (sfRunning || !sfPlan || sfPlan === 'error') return;
    const leadIds = sfIdsRef.current;
    const campaignId = sfCampaignId || null;
    sfCancelRef.current = false;
    setSfRunning(true);
    setSfFinal(null);
    setSfStopCode(null);
    setSfProgress({ started: 0, total: leadIds.length, results: {} });

    const { results, stopCode } = await runSalesforceBulk({
      leadIds,
      plan: sfPlan,
      campaignId,
      token,
      dirtyLeadIds,
      isCancelled: () => sfCancelRef.current,
      isMounted: () => sfMountedRef.current,
      onProgress: (progress) => { if (sfMountedRef.current) setSfProgress(progress); },
    });

    // WHAT IS IN SALESFORCE IS READ BACK, never inferred from this browser: a request whose
    // answer was lost may well have created the Lead, and calling that a failure would be
    // the one lie this dialog must not tell.
    const finalPlan = await fetchSalesforcePlan(campaignId || '');
    if (!sfMountedRef.current) return;
    setSfFinal(mergeFinalResults({ leadIds, finalPlan, localResults: results }));
    setSfStopCode(stopCode);
    setSfRunning(false);
    sfCancelRef.current = false;
    // The selection is deliberately left exactly as the user made it.
  };

  const sfConnected = sfPlan && sfPlan !== 'error'
    && sfPlan.connection_status === 'connected';
  const sfSummary = (sfPlan && sfPlan !== 'error')
    ? planSummary(sfPlan, sfIdsRef.current, dirtyLeadIds)
    : null;
  const sfTally = runTally(sfProgress?.results);
  const sfCampaignList = Array.isArray(sfCampaigns) ? sfCampaigns : [];
  const sfFinalLines = sfFinal ? summaryLines(sfFinal, sfIdsRef.current) : [];
  const sfLeadName = (leadId) => (searchResults?.leads || [])
    .find((item) => item.id === leadId)?.shop_name || leadId;

  // Show loading state when loading search from URL
  if (loadingSearch) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  // Show results if we have them
  if (searchResults) {
    const approvedCount = searchResults.leads.filter(l => l.decision === 'approved').length;
    const rejectedCount = searchResults.leads.filter(l => l.decision === 'rejected').length;
    const pendingCount = searchResults.leads.filter(l => !l.decision).length;
    // Whether the review actions are worth offering for THIS selection. Both handlers act
    // on PENDING leads only — they skip any lead that already carries a decision — so a
    // selection with nothing pending left has nothing either button could change, and
    // neither is shown. Display only: who may be approved or rejected, and what happens
    // when they are, is unchanged.
    const selectedLeads = searchResults.leads.filter(l => selectedLeadIds.has(l.id));
    const hasPendingSelected = selectedLeads.some(l => !l.decision);
    const recommendedCount = searchResults.leads.filter(l => l.recommended && !l.decision).length;

    return (
      <div className="space-y-6 relative" data-testid="search-results-page">
        <WorldGlobe size={320} className="hidden lg:block opacity-70" style={{ top: '-55px', right: '-30px' }} />
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Search Results</h2>
            <p className="text-slate-400 mt-1">
              Found {searchResults.leads.length} leads · {new Date(searchResults.created_at).toLocaleString()}
            </p>
            <div className="flex gap-4 mt-2">
              <span className="text-sm text-green-400">{approvedCount} approved</span>
              <span className="text-sm text-red-400">{rejectedCount} rejected</span>
              <span className="text-sm text-slate-400">{pendingCount} pending</span>
              {recommendedCount > 0 && (
                <span className="text-sm text-amber-400 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {recommendedCount} recommended
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Go to Export CTA - shows only when review is complete */}
            {pendingCount === 0 && approvedCount > 0 && (
              <Button 
                onClick={() => navigate(`/export?search=${searchResults.id}`)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="go-to-export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Go to Export
              </Button>
            )}
            {/* P4.5: Approve / Reject all pending — only when there are pending leads */}
            {pendingCount > 0 && (
              <>
                <Button
                  onClick={handleApproveAllPending}
                  disabled={processingBulk}
                  variant="outline"
                  className="border-green-500/40 text-green-300 hover:bg-green-500/15"
                  data-testid="approve-all-pending-btn"
                >
                  {processingBulk ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Approve all pending ({pendingCount})
                </Button>
                <Button
                  onClick={handleOpenRejectAllPending}
                  disabled={processingBulk}
                  variant="outline"
                  className="border-red-500/40 text-red-400 hover:bg-red-500/15"
                  data-testid="reject-all-pending-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject all pending ({pendingCount})
                </Button>
              </>
            )}
            {/* Reset Review - shows when there are decisions */}
            {(approvedCount > 0 || rejectedCount > 0) && (
              <Button 
                onClick={() => setResetReviewDialogOpen(true)}
                variant="outline"
                className="text-slate-300"
                data-testid="reset-review-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset review
              </Button>
            )}
            {/* P4.7: Bulk enrichment — visible only when at least one approved lead is not yet enriched */}
            {!bulkEnriching && bulkEnrichEligible && bulkEnrichEligible.lead_ids.length > 0 && (
              <Button
                onClick={handleBulkEnrich}
                variant="outline"
                className="border-blue-500/40 text-blue-300 hover:bg-blue-500/15"
                data-testid="bulk-enrich-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Enrich all approved ({bulkEnrichEligible.lead_ids.length})
              </Button>
            )}
            {bulkEnriching && (
              <Button
                onClick={handleBulkEnrichCancel}
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/15"
                data-testid="bulk-enrich-cancel-btn"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
            <Button 
              onClick={handleNewSearch}
              variant="outline"
              data-testid="new-search-button"
            >
              New Search
            </Button>
          </div>
        </div>

        {/* Review completed banner - shows when pending = 0 and approved > 0 */}
        {pendingCount === 0 && approvedCount > 0 && (
          <div 
            className="bg-green-500/10 border border-green-500/40 rounded-lg p-4 flex items-center justify-between"
            data-testid="review-complete-banner"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-500/15 flex items-center justify-center">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium text-green-300">Review completed</p>
                <p className="text-sm text-green-400">
                  You can now export your {approvedCount} approved lead{approvedCount !== 1 ? 's' : ''}.
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate(`/export?search=${searchResults.id}`)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="banner-export-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Go to Export
            </Button>
          </div>
        )}

        {/* Status message from search pipeline */}
        {searchResults.status_message && (
          <div className="bg-blue-500/10 border border-blue-500/40 rounded-lg p-4" data-testid="search-status-message">
            <p className="text-sm text-blue-300">{searchResults.status_message}</p>
          </div>
        )}

        {/* P4.7: Bulk Enrichment progress bar */}
        {bulkEnriching && (
          <div
            className="bg-blue-500/10 border border-blue-500/40 rounded-lg p-4"
            data-testid="bulk-enrich-progress"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                <p className="text-sm font-medium text-blue-300">
                  Enriching {bulkEnrichProgress.done} of {bulkEnrichProgress.total}...
                </p>
              </div>
              <p className="text-xs text-slate-400">
                {bulkEnrichProgress.succeeded} succeeded
                {bulkEnrichProgress.failed > 0 && ` · ${bulkEnrichProgress.failed} failed`}
              </p>
            </div>
            <div className="w-full bg-blue-500/15 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{
                  width: `${bulkEnrichProgress.total > 0
                    ? Math.round((bulkEnrichProgress.done / bulkEnrichProgress.total) * 100)
                    : 0}%`
                }}
                data-testid="bulk-enrich-progress-bar"
              />
            </div>
          </div>
        )}

        {/* Bulk Actions Bar - shows when leads are selected */}
        {selectedLeadIds.size > 0 && (
          <div
            className="sticky top-16 z-50 border border-blue-500/40 rounded-lg p-3 flex items-center justify-between"
            style={{
              // Opaque page-color base + the original bg-blue-500/10 tint layered on top,
              // so the (now sticky) bar isn't see-through over the table rows beneath it.
              // Same colors as before (--bg-page + blue-500/10) — appearance unchanged.
              backgroundColor: 'var(--bg-page)',
              backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.1))',
            }}
            data-testid="bulk-actions-bar"
          >
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-300">
                {selectedLeadIds.size} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLeadIds(new Set())}
                className="text-blue-400 hover:text-blue-300 h-7"
              >
                Clear
              </Button>
              {pendingCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAllPending}
                  className="text-blue-400 hover:text-blue-300 h-7"
                  data-testid="select-all-pending-btn"
                >
                  Select all pending ({pendingCount})
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasPendingSelected && (
                <Button
                  size="sm"
                  onClick={handleBulkApprove}
                  disabled={processingBulk}
                  className="bg-green-600 hover:bg-green-700 h-8"
                  data-testid="bulk-approve-btn"
                >
                  {processingBulk ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Approve selected
                </Button>
              )}
              {hasPendingSelected && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenRejectSelected}
                  disabled={processingBulk}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/15 h-8"
                  data-testid="bulk-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject selected
                </Button>
              )}
              {/* P-SF5: the selection's third action. Absent entirely when this company has
                  no Salesforce integration, so the bar is what it always was. */}
              {salesforceOffered && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openSalesforceDialog}
                  disabled={processingBulk || sfRunning}
                  className="border-blue-500/40 text-blue-300 hover:bg-blue-500/15 h-8"
                  data-testid="bulk-salesforce-btn"
                >
                  <Cloud className="h-4 w-4 mr-1" />
                  Send to Salesforce
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Results Table */}
        <Card className="world-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="results-table">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <Checkbox
                        checked={selectedLeadIds.size === searchResults.leads.length && searchResults.leads.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Shop</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Website</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Country</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Category</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Rating</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Quality</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {searchResults.leads.map((lead) => (
                    <>
                      <tr 
                        key={lead.id} 
                        className={`hover:bg-white/5 ${
                          lead.decision === 'approved' ? 'bg-green-500/10' : 
                          lead.decision === 'rejected' ? 'bg-red-500/10' : 
                          selectedLeadIds.has(lead.id) ? 'bg-blue-500/10' : ''
                        }`}
                        data-testid={`lead-row-${lead.id}`}
                      >
                        <td className="px-4 py-4">
                          <Checkbox
                            checked={selectedLeadIds.has(lead.id)}
                            onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                            data-testid={`select-lead-${lead.id}`}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                              lead.decision === 'approved' ? 'bg-green-500/15' :
                              lead.decision === 'rejected' ? 'bg-red-500/15' : 
                              lead.recommended ? 'bg-amber-500/15' : 'bg-blue-500/15'
                            }`}>
                              {lead.decision === 'approved' ? (
                                <Check className="h-4 w-4 text-green-400" />
                              ) : lead.decision === 'rejected' ? (
                                <XCircle className="h-4 w-4 text-red-400" />
                              ) : lead.recommended ? (
                                <Sparkles className="h-4 w-4 text-amber-400" />
                              ) : (
                                <Globe className="h-4 w-4 text-blue-400" />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium text-white">{lead.shop_name}</span>
                              {/* P4.7b + P4.8b: enriched state badge — only for approved leads, emoji icons */}
                              {lead.decision === 'approved' && bulkEnrichEligible && (
                                bulkEnrichEligible.lead_ids.includes(lead.id) ? (
                                  <span
                                    className="text-xs text-slate-400 inline-flex items-center gap-1 mt-0.5"
                                    data-testid={`enrichment-pending-${lead.id}`}
                                  >
                                    <span aria-hidden="true">⏳</span>
                                    Pending enrichment
                                  </span>
                                ) : (
                                  <span
                                    className="text-xs text-green-300 inline-flex items-center gap-1 mt-0.5 font-medium"
                                    data-testid={`enrichment-enriched-${lead.id}`}
                                  >
                                    <span aria-hidden="true">✅</span>
                                    Enriched
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <a 
                            href={lead.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          >
                            {lead.website.replace('https://www.', '')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{lead.country}</td>
                        <td className="px-6 py-4">
                          <Badge variant="secondary">{lead.category}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                              <span className="font-medium">{lead.rating}</span>
                            </div>
                            {lead.recommended && (
                              <Badge
                                variant="outline"
                                className="bg-amber-500/10 text-amber-300 border-amber-500/40 text-xs h-5 px-1.5"
                                data-testid={`recommended-badge-${lead.id}`}
                              >
                                Recommended
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  lead.quality_score >= 70 ? 'bg-green-500' : 
                                  lead.quality_score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${lead.quality_score}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-200">{lead.quality_score}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {processingDecision === lead.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                          ) : lead.decision ? (
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={lead.decision === 'approved' ? 'world-badge-approved' : 'world-badge-rejected'}
                              >
                                {lead.decision === 'approved' ? 'Approved' : 'Rejected'}
                              </Badge>
                              {lead.decision === 'approved' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleEnrichmentPanel(lead.id)}
                                  className={`h-7 px-2 ${expandedLeadId === lead.id ? 'bg-white/10' : ''}`}
                                  data-testid={`expand-btn-${lead.id}`}
                                >
                                  {/* 2.0X — the spinner REPLACES the chevron rather than
                                      sitting beside it: same button, same footprint, so a
                                      lead starting to enrich shifts nothing in the list.
                                      The button still opens the panel, which is how the
                                      user reaches the in-progress view mid-run. */}
                                  {isLeadEnriching(lead.id) ? (
                                    <Loader2 className="h-3 w-3 animate-spin text-blue-400" />
                                  ) : expandedLeadId === lead.id ? (
                                    <ChevronUp className="h-3 w-3" />
                                  ) : (
                                    <ChevronDown className="h-3 w-3" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUndo(lead.id)}
                                className="h-7 px-2 text-slate-400 hover:text-white"
                                data-testid={`undo-btn-${lead.id}`}
                              >
                                <Undo2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApprove(lead.id)}
                                className="h-8 px-3 text-green-400 border-green-500/40 hover:bg-green-500/15 hover:text-green-300"
                                data-testid={`approve-btn-${lead.id}`}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRejectClick(lead)}
                                className="h-8 px-3 text-red-400 border-red-500/40 hover:bg-red-500/15 hover:text-red-300"
                                data-testid={`reject-btn-${lead.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {/* Enrichment Panel - only for approved leads when expanded */}
                      {lead.decision === 'approved' && expandedLeadId === lead.id && (
                        <EnrichmentPanel 
                          key={`enrichment-${lead.id}`}
                          lead={lead} 
                          token={token}
                          onClose={() => setExpandedLeadId(null)}
                          onEnrichmentChanged={fetchBulkEnrichEligibility}
                          onEnrichmentCleared={handleEnrichmentClearedLocal}
                          onDirtyChange={handleLeadDirtyChange}
                        />
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Reject Reason Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Lead</DialogTitle>
              <DialogDescription>
                {selectedLeadForReject && (
                  <>Rejecting: <strong>{selectedLeadForReject.shop_name}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <RejectionReasonFields
                idPrefix="reject"
                code={rejectReasonCode}
                onCodeChange={setRejectReasonCode}
                note={rejectReason}
                onNoteChange={setRejectReason}
                notePlaceholder="Anything to add?"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={!rejectionReady(rejectReasonCode, rejectReason)}
                data-testid="confirm-reject-btn"
              >
                Reject Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Reject Confirmation Dialog */}
        <AlertDialog open={bulkRejectDialogOpen} onOpenChange={(open) => {
          setBulkRejectDialogOpen(open);
          if (!open) {
            setBulkRejectReason('');
            setBulkRejectReasonCode('');
            setBulkRejectTargetIds([]);
          }
        }}>
          <AlertDialogContent data-testid="bulk-reject-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reject selected leads?</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to reject {bulkRejectTargetIds.length || selectedLeadIds.size} lead{(bulkRejectTargetIds.length || selectedLeadIds.size) !== 1 ? 's' : ''}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <RejectionReasonFields
                idPrefix="bulk-reject"
                code={bulkRejectReasonCode}
                onCodeChange={setBulkRejectReasonCode}
                note={bulkRejectReason}
                onNoteChange={setBulkRejectReason}
                notePlaceholder="The same reason and note apply to all selected leads"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel 
                disabled={processingBulk}
                data-testid="bulk-reject-cancel-btn"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkRejectConfirm}
                disabled={processingBulk || !rejectionReady(bulkRejectReasonCode, bulkRejectReason)}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                data-testid="bulk-reject-confirm-btn"
              >
                {processingBulk ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  'Reject'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* P-SF5: Send the selected leads to Salesforce. The plan comes from the server, the
            writes are the single-lead endpoints, one lead at a time. */}
        <Dialog
          open={sfDialogOpen}
          onOpenChange={(open) => { if (!open && !sfRunning) setSfDialogOpen(false); }}
        >
          <DialogContent className="sm:max-w-lg" data-testid="salesforce-bulk-dialog">
            <DialogHeader>
              <DialogTitle>
                {`Send ${sfIdsRef.current.length} selected lead${sfIdsRef.current.length !== 1 ? 's' : ''} to Salesforce`}
              </DialogTitle>
              <DialogDescription data-testid="salesforce-bulk-saved-note">
                Uses saved lead and enrichment data.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 text-sm">
              {sfPlanLoading && !sfPlan && (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Reading the current Salesforce state...
                </div>
              )}

              {sfPlan === 'error' && !sfFinal && (
                <p className="text-amber-300" data-testid="salesforce-bulk-plan-error">
                  Could not read the current Salesforce state. Nothing has been sent.
                </p>
              )}

              {sfPlan && sfPlan !== 'error' && !sfConnected && (
                <p className="text-slate-400" data-testid="salesforce-bulk-not-connected">
                  {sfPlan.connection_status === 'reauth_required'
                    ? 'Salesforce needs to be authorised again. An administrator can do that in Settings.'
                    : 'Salesforce is not connected. An administrator can connect it in Settings.'}
                </p>
              )}

              {/* The plan, before anything is sent. */}
              {sfSummary && !sfFinal && !sfRunning && (
                <ul className="space-y-1" data-testid="salesforce-bulk-plan">
                  <li className="text-slate-200" data-testid="salesforce-bulk-count-ready">
                    {`${sfSummary.counts.ready} ready to create`}
                  </li>
                  {sfSummary.counts.already_created > 0 && (
                    <li className="text-slate-400" data-testid="salesforce-bulk-count-already">
                      {`${sfSummary.counts.already_created} already in Salesforce`}
                    </li>
                  )}
                  {sfSummary.counts.duplicate > 0 && (
                    <li className="text-slate-400" data-testid="salesforce-bulk-count-duplicate">
                      {`${sfSummary.counts.duplicate} duplicate — review in Salesforce`}
                    </li>
                  )}
                  {sfSummary.counts.manual > 0 && (
                    <li className="text-slate-400" data-testid="salesforce-bulk-count-manual">
                      {`${sfSummary.counts.manual} need a manual check`}
                    </li>
                  )}
                  {sfSummary.counts.unsaved_changes > 0 && (
                    <li className="text-amber-300" data-testid="salesforce-bulk-count-unsaved">
                      {`${sfSummary.counts.unsaved_changes} with unsaved changes — save first`}
                    </li>
                  )}
                  {sfSummary.counts.not_eligible > 0 && (
                    <li className="text-slate-400" data-testid="salesforce-bulk-count-not-eligible">
                      {`${sfSummary.counts.not_eligible} not eligible`}
                    </li>
                  )}
                </ul>
              )}

              {/* The Campaign, optional, from the org's own active list. */}
              {sfConnected && !sfFinal && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Campaign (optional)</Label>
                  {sfCampaigns === 'unavailable' ? (
                    <p className="text-slate-400" data-testid="salesforce-bulk-campaigns-unavailable">
                      Campaigns unavailable — leads can still be created.
                    </p>
                  ) : (
                    <select
                      value={sfCampaignId}
                      onChange={(e) => handleSalesforceCampaignChange(e.target.value)}
                      disabled={sfRunning || sfCampaignList.length === 0}
                      className="h-9 w-full rounded-md border border-white/15 bg-transparent px-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400/60 disabled:opacity-50"
                      data-testid="salesforce-bulk-campaign-select"
                    >
                      <option value="">No campaign</option>
                      {sfCampaignList.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {[campaign.name, campaign.status].filter(Boolean).join(' · ')}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* The run. */}
              {sfRunning && sfProgress && (
                <div className="space-y-1" data-testid="salesforce-bulk-progress">
                  <p className="text-slate-200 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {`Sending ${sfProgress.started} of ${sfProgress.total}...`}
                  </p>
                  <p className="text-slate-400">{`${sfTally.completed} completed`}</p>
                  {sfTally.attention > 0 && (
                    <p className="text-amber-300">{`${sfTally.attention} needs attention`}</p>
                  )}
                  {sfCancelRef.current && (
                    <p className="text-slate-400">Finishing the current lead, then stopping.</p>
                  )}
                </div>
              )}

              {/* The summary, rebuilt from what the server holds. */}
              {sfFinal && (
                <div className="space-y-2" data-testid="salesforce-bulk-summary">
                  <ul className="space-y-1">
                    {sfFinalLines.map((line) => (
                      <li
                        key={line.key}
                        className="text-slate-200 flex items-center gap-2"
                        data-testid={`salesforce-bulk-result-${line.key}`}
                      >
                        {line.key.startsWith('created') || line.key.startsWith('already_created') ? (
                          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-amber-400" />
                        )}
                        {`${line.count} ${line.label}`}
                      </li>
                    ))}
                  </ul>
                  {sfStopCode === 'salesforce_connection_changed' && (
                    <p className="text-amber-300" data-testid="salesforce-bulk-stopped">
                      Salesforce was connected to a different org during the run, so the rest
                      of the selection was not sent.
                    </p>
                  )}
                  {/* The leads that want a look, named. Not a table: a short list. */}
                  {sfIdsRef.current.filter((id) => sfFinal[id] && needsAttention(sfFinal[id]))
                    .slice(0, 12).map((id) => (
                      <p
                        key={id}
                        className="text-xs text-slate-400"
                        data-testid={`salesforce-bulk-detail-${id}`}
                      >
                        {`${sfLeadName(id)} — ${sfFinal[id].lead.replace(/_/g, ' ')}`}
                        {sfFinal[id].leadCode ? ` (${sfFinal[id].leadCode})` : ''}
                        {sfFinal[id].campaign === 'failed'
                          ? `, campaign not added${sfFinal[id].campaignCode ? ` (${sfFinal[id].campaignCode})` : ''}`
                          : ''}
                      </p>
                    ))}
                </div>
              )}
            </div>

            <DialogFooter>
              {sfRunning ? (
                <Button
                  variant="outline"
                  onClick={handleSalesforceCancel}
                  className="border-white/15"
                  data-testid="salesforce-bulk-cancel-btn"
                >
                  Cancel
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setSfDialogOpen(false)}
                    className="border-white/15"
                    data-testid="salesforce-bulk-close-btn"
                  >
                    {sfFinal ? 'Close' : 'Cancel'}
                  </Button>
                  {!sfFinal && (
                    <Button
                      onClick={handleSendToSalesforce}
                      disabled={!sfConnected || sfPlanLoading || !sfSummary
                        || sfSummary.counts.ready + sfSummary.counts.already_created === 0}
                      className="world-btn-primary"
                      data-testid="salesforce-bulk-confirm-btn"
                    >
                      <Cloud className="h-4 w-4 mr-1" />
                      Send to Salesforce
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Review Confirmation Dialog */}
        <AlertDialog open={resetReviewDialogOpen} onOpenChange={setResetReviewDialogOpen}>
          <AlertDialogContent data-testid="reset-review-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset review?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>This will move all approved and rejected leads back to pending.</p>
                <p className="font-medium text-slate-300">Learning will NOT be affected.</p>
                <p className="text-amber-400">This action cannot be undone.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                disabled={processingReset}
                data-testid="reset-cancel-btn"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleResetReview}
                disabled={processingReset}
                className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
                data-testid="reset-confirm-btn"
              >
                {processingReset ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Search Form
  return (
    <div className="space-y-6 relative" data-testid="search-page">
      <WorldGlobe size={320} className="hidden lg:block opacity-70" style={{ top: '-55px', right: '-30px' }} />
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
              <Search className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Search Leads</CardTitle>
              <CardDescription className="text-slate-400">
                Configure your search parameters to find B2C e-commerce leads
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Categories (Tag Input) */}
            <div className="space-y-2">
              <Label htmlFor="categories" className="text-slate-200 font-medium">
                Categories <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {categories.map((cat, index) => (
                  <Badge key={cat} variant="secondary" className="flex items-center gap-1">
                    {cat}
                    <button
                      onClick={() => handleRemoveCategory(index)}
                      className="ml-1 hover:text-red-500"
                      data-testid={`remove-category-${cat}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              {/* P8: the macro picker is an overlay rendered in a portal — it never affects
                  the flow of the form, so nothing below it moves when it opens. The input is
                  the popover ANCHOR (so the panel matches its width and sits right under it),
                  while the small in-input button is the TRIGGER. */}
              <Popover open={macroOpen} onOpenChange={setMacroOpen}>
                <PopoverAnchor asChild>
                  <div className="relative">
                    <Input
                      placeholder="Type category and press Enter"
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      onKeyDown={handleAddCategory}
                      className="pr-11"
                      data-testid="category-input"
                    />
                    <PopoverTrigger asChild>
                      {/* The button is a transparent 44x44 hit box (comfortable on touch) that
                          overflows the input only with empty space; the VISIBLE chrome is the
                          inner 36x32 span, which stays inside the input's borders. */}
                      <button
                        type="button"
                        aria-label="Browse macro-categories"
                        title="Browse macro-categories"
                        className="group absolute right-0 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center focus:outline-none"
                        data-testid="macro-category-trigger"
                      >
                        <span className="h-8 w-9 flex items-center justify-center rounded-md text-slate-400 transition-colors group-hover:text-slate-100 group-hover:bg-white/10 group-focus-visible:ring-2 group-focus-visible:ring-blue-500/60">
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${macroOpen ? 'rotate-180' : ''}`}
                          />
                        </span>
                      </button>
                    </PopoverTrigger>
                  </div>
                </PopoverAnchor>
                <PopoverContent
                  align="start"
                  sideOffset={6}
                  className="w-[var(--radix-popover-trigger-width)] max-h-72 overflow-y-auto p-1"
                  data-testid="macro-category-popover"
                >
                  {MACRO_CATEGORIES.map(macro => {
                    const selected = categoryKeys.includes(macro.key) || categories.includes(macro.label);
                    // P10: at the cap, macros that aren't already chosen can't be added.
                    const blocked = selected || categoriesFull;
                    // P12b: the edit control is a SIBLING of the select control, never nested
                    // inside it. `blocked` disables selection only — customising an already
                    // selected macro, or one past the cap, stays available. The only thing that
                    // disables editing is having no market selected yet.
                    return (
                      <div key={macro.key} className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={blocked}
                          onClick={() => handleAddMacroCategory(macro)}
                          className={`flex-1 min-w-0 min-h-[44px] px-3 py-2 flex items-center justify-between gap-2 rounded-md text-sm text-left transition-colors ${
                            blocked
                              ? 'text-slate-500 cursor-not-allowed'
                              : 'text-slate-200 hover:bg-white/10'
                          }`}
                          data-testid={`macro-category-${macro.key}`}
                        >
                          <span className="truncate">{macro.label}</span>
                          {selected && <Check className="h-4 w-4 shrink-0 text-blue-400" />}
                        </button>
                        <button
                          type="button"
                          disabled={!editorCountry}
                          onClick={() => {
                            setMacroOpen(false);
                            setEditorMacro(macro);
                          }}
                          className={`relative h-11 w-11 shrink-0 flex items-center justify-center rounded-md transition-colors ${
                            editorCountry
                              ? 'text-slate-400 hover:text-slate-100 hover:bg-white/10'
                              : 'text-slate-600 cursor-not-allowed'
                          }`}
                          aria-label={
                            editorCountry
                              ? `Customise sub-niches for ${macro.label}`
                              : 'Select a market first to customise sub-niches'
                          }
                          title={
                            editorCountry
                              ? `Customise sub-niches for ${macro.label}`
                              : 'Select a market first'
                          }
                          data-testid={`macro-edit-${macro.key}`}
                        >
                          <SlidersHorizontal className="h-4 w-4" />
                          {customisedMacroKeys.includes(macro.key) && (
                            <span
                              className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-blue-400"
                              data-testid={`macro-customised-${macro.key}`}
                            />
                          )}
                        </button>
                      </div>
                    );
                  })}
                  {!editorCountry && (
                    <p className="px-3 pt-2 pb-1 mt-1 border-t border-white/10 text-xs text-slate-500">
                      Select a market first to customise sub-niches.
                    </p>
                  )}
                </PopoverContent>
              </Popover>
              <p className="text-sm text-slate-400">
                Add one or more product categories (e.g. Fashion, Electronics).
                {' '}Up to {MAX_CATEGORIES_PER_SEARCH} per search.
              </p>
              {/* P10/P11: leads are a per-category consequence, so spell out the resulting
                  ceiling. "up to" is deliberate — a category with a thin pool returns fewer. */}
              {categories.length > 0 && (
                <p className="text-sm text-blue-300" data-testid="category-lead-ceiling">
                  {categories.length} {categories.length === 1 ? 'category' : 'categories'}
                  {' × '}{LEADS_PER_CATEGORY} leads = up to {categories.length * LEADS_PER_CATEGORY} leads
                </p>
              )}
            </div>

            {/* M4-A: Target market — ONE compact, searchable picker in place of the country grid.
                The catalogue is MARKET_COUNTRIES (M4-C added Switzerland); the engine has always
                searched exactly one market, so the control says so instead of offering checkboxes. */}
            <div className="space-y-2">
              <Label htmlFor="target-market" className="text-slate-200 font-medium">
                Target market <span className="text-red-500">*</span>
              </Label>
              <MarketPicker
                id="target-market"
                options={MARKET_COUNTRIES}
                value={countries[0] || null}
                onChange={handleMarketChange}
              />
              <p className="text-sm text-slate-400">
                One market per search: the country where you want to find e-commerce leads.
              </p>
            </div>

            {/* P11: the "Number of leads" control was removed — leads are a consequence of
                how many categories are selected (LEADS_PER_CATEGORY each), shown by the
                ceiling line under the categories block. */}

            {/* Include Marketplaces Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-200 font-medium">Include marketplaces</Label>
                <p className="text-sm text-slate-400">
                  {includeMarketplaces ? 'Yes' : 'No'} – marketplace sellers {includeMarketplaces ? 'will be included' : 'will be excluded'}
                </p>
              </div>
              <Switch
                checked={includeMarketplaces}
                onCheckedChange={setIncludeMarketplaces}
                data-testid="include-marketplaces-toggle"
              />
            </div>

            {/* S12 slice 1: Include Brand Shops Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-200 font-medium">Include brand shops</Label>
                <p className="text-sm text-slate-400">
                  {includeBrandShops ? 'Yes' : 'No'} – official brand and manufacturer stores {includeBrandShops ? 'will be included' : 'will be excluded'}
                </p>
              </div>
              <Switch
                checked={includeBrandShops}
                onCheckedChange={setIncludeBrandShops}
                data-testid="include-brand-shops-toggle"
              />
            </div>

            {/* Blacklist Upload (P6.8) — persistent, company-scoped domain blacklist */}
            <div className="space-y-2">
              <Label className="text-slate-200 font-medium">Blacklist upload</Label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-md cursor-pointer hover:bg-white/5">
                  {blacklistUploading ? (
                    <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 text-slate-400" />
                  )}
                  <span className="text-sm text-slate-300">
                    {blacklistUploading
                      ? 'Uploading...'
                      : blacklistInfo?.source_filename || 'Choose .xlsx or .csv file'}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.csv"
                    onChange={handleFileChange}
                    disabled={blacklistUploading}
                    className="hidden"
                    data-testid="blacklist-upload"
                  />
                </label>
                {blacklistInfo && !blacklistUploading && (
                  <button
                    onClick={handleRemoveBlacklist}
                    className="text-slate-400 hover:text-red-500"
                    data-testid="remove-blacklist"
                    title="Clear blacklist"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {blacklistInfo && !blacklistUploading && (
                <p className="text-xs text-slate-400" data-testid="blacklist-summary">
                  {blacklistInfo.domains_count} domain{blacklistInfo.domains_count === 1 ? '' : 's'} blocked from results
                </p>
              )}
            </div>

            {/* Agent Check */}
            <div className="space-y-2">
              <Label htmlFor="agentCheck" className="text-slate-200 font-medium">
                Agent check (optional)
              </Label>
              <Textarea
                id="agentCheck"
                placeholder="Optional guidance for lead qualification..."
                value={agentCheck}
                onChange={(e) => setAgentCheck(e.target.value.slice(0, 300))}
                rows={3}
                data-testid="agent-check-input"
                aria-describedby="agentCheckHelp"
              />
              <p className="text-sm text-slate-400">
                {agentCheck.length}/300 characters · Optional guidance for lead qualification
              </p>
              {/* AC-052: what the guidance does, and what it cannot do. */}
              <p id="agentCheckHelp" className="text-xs text-slate-500" data-testid="agent-check-help">
                Describe the shops you want, e.g. &quot;prefer computer specialists&quot; or &quot;only
                independent shops&quot;. Strict wording (only, exclude) can return fewer than 10 leads. It
                cannot make a non-shop or a B2B-only seller eligible, and it does not override the
                marketplace and brand shop switches.
              </p>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                onClick={handleSearch}
                disabled={!isFormValid || isSearching}
                className="w-full sm:w-auto world-btn-primary"
                data-testid="start-search-button"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {searchProgress || 'Searching...'}
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Start Search
                  </>
                )}
              </Button>
              {isSearching && (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-100 rounded-lg">
                  <p className="text-sm text-blue-300 font-medium">
                    {searchProgress || 'Search in progress...'}
                  </p>
                  <p className="text-xs text-blue-400 mt-1">
                    This may take 1-3 minutes. You can navigate away - search will continue in background.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* P12b: sub-niche editor. Rendered outside the Popover so the dialog owns focus,
          and only mounted while a macro is being edited so it always fetches fresh. */}
      {editorMacro && editorCountry && (
        <SubnicheEditorDialog
          open={!!editorMacro}
          onOpenChange={(next) => { if (!next) setEditorMacro(null); }}
          macro={editorMacro}
          country={editorCountry}
          onSaved={loadCustomisedMacros}
        />
      )}
    </div>
  );
};

export default Dashboard;
